import React, { useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { money, statusLabel, statusColor } from "@/lib/format";
import { Ticket, Search, Trophy, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

export default function Validate() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(false);

  const search = async (e) => {
    e?.preventDefault();
    if (!q.trim()) return;
    setLoading(true);
    try {
      const res = await api.get("/public/validate", { params: { q } });
      setResults(res.data.results);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-xl border-b border-neutral-100">
        <div className="max-w-lg mx-auto px-4 h-16 flex items-center gap-3">
          <Link to="/"><Button variant="ghost" size="icon" className="rounded-xl"><ArrowLeft size={18} /></Button></Link>
          <span className="font-display font-extrabold text-lg">Validar mis números</span>
        </div>
      </header>

      <div className="max-w-lg mx-auto px-4 py-8">
        <div className="bg-white rounded-2xl border border-neutral-200 p-6 shadow-[0_8px_32px_rgba(0,0,0,0.04)]">
          <div className="w-12 h-12 rounded-2xl bg-[#E6F7FE] text-[#00AEEF] flex items-center justify-center"><Search size={24} /></div>
          <h1 className="font-display font-bold text-xl mt-4">Consulta tu participación</h1>
          <p className="text-sm text-neutral-500 mt-1">Ingresa el email o teléfono que usaste al comprar.</p>
          <form onSubmit={search} className="flex gap-2 mt-5 max-sm:flex-col">
            <Input data-testid="validate-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="correo@ejemplo.com o 8888-8888" className="rounded-xl max-sm:h-12 max-sm:text-base" />
            <Button data-testid="validate-search-btn" type="submit" disabled={loading} className="bg-[#00AEEF] hover:bg-[#0092CC] rounded-xl max-sm:h-12 max-sm:w-full">{loading ? "..." : "Buscar"}</Button>
          </form>
        </div>

        {results && (
          <div className="mt-6 space-y-4" data-testid="validate-results">
            {results.length === 0 ? (
              <p className="text-center text-neutral-500 py-8">No encontramos participaciones con ese dato.</p>
            ) : results.map((r) => (
              <div key={r.order_id} className="bg-white rounded-2xl border border-neutral-200 p-5" data-testid={`validate-order-${r.order_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <h3 className="font-display font-bold">{r.raffle_title}</h3>
                    <p className="text-xs text-neutral-400 mt-0.5">Evento: {r.draw_date ? new Date(r.draw_date).toLocaleString("es-CR") : "Por definir"}</p>
                  </div>
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${statusColor(r.status)}`}>{statusLabel(r.status)}</span>
                </div>
                <div className="flex flex-wrap gap-1.5 mt-4">
                  {r.numbers.map((n) => {
                    const wnums = (r.winning_numbers && r.winning_numbers.length) ? r.winning_numbers : (r.winning_number != null ? [r.winning_number] : []);
                    const isWin = wnums.includes(n);
                    return <span key={n} data-testid={`validate-num-${r.order_id}-${n}`} className={`text-sm font-semibold px-2.5 py-1 rounded-lg ${isWin ? "bg-amber-400 text-amber-950" : "bg-neutral-100 text-neutral-700"}`}>#{n}</span>;
                  })}
                </div>
                <p className="text-sm text-neutral-500 mt-3">Monto: {money(r.amount, r.currency)}</p>
                {r.raffle_status === "drawn" && (() => {
                  const wnums = (r.winning_numbers && r.winning_numbers.length) ? r.winning_numbers : (r.winning_number != null ? [r.winning_number] : []);
                  const ordinal = (p) => (p === 1 ? "1er" : p === 2 ? "2º" : p === 3 ? "3º" : `${p}º`);
                  const placeOf = (num) => (r.winners || []).find((w) => w.number === num)?.place;
                  const label = (num) => { const p = placeOf(num); return p ? `${ordinal(p)} lugar · #${num}` : `#${num}`; };
                  if (r.won) {
                    const myWins = r.numbers.filter((n) => wnums.includes(n));
                    return (
                      <div data-testid={`validate-won-${r.order_id}`} className="mt-3 rounded-xl p-3 text-sm font-medium flex items-start gap-2 bg-amber-50 text-amber-800">
                        <Trophy size={16} className="mt-0.5 shrink-0" />
                        <span>¡Ganaste! {myWins.map(label).join(" · ")}</span>
                      </div>
                    );
                  }
                  return (
                    <div data-testid={`validate-lost-${r.order_id}`} className="mt-3 rounded-xl p-3 text-sm font-medium flex items-start gap-2 bg-neutral-50 text-neutral-600">
                      <span>{wnums.length > 1 ? "Números ganadores" : "Número ganador"}: {wnums.map(label).join(" · ")}. Esta vez no fue.</span>
                    </div>
                  );
                })()}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
